import React, { useState, useEffect } from "react";
import AdminMessagesList from "./AdminMessagesList";
import "../static/AdminDashboard.css";
import UserManagement from "./Admin/UserManagement";
import Footer from "../components/common/Footer";
import TestimonialsManagement from "./TestimonialsManagement";
import CourseAdminManagement from "./CourseAdminManagement";
import AuditLogs from "./AuditLogs";
import LeadershipAdminPanel from "./Admin/LeadershipAdminPanel";

const menuItems = [
  { id: "overview", label: "Overview", icon: "📊" },
  { id: "users", label: "Users", icon: "👤" },
  { id: "courses", label: "Courses", icon: "📚" },
  { id: "testimonials", label: "Testimonials", icon: "💬" },
  { id: "analytics", label: "Analytics", icon: "📈" },
  { id: "messages", label: "Messages", icon: "💬" },
  { id: "leadership", label: "Leadership", icon: "🏛️" },  // ← NEW
  { id: "settings", label: "Settings", icon: "⚙️" },
  { id: "audit", label: "Audit Logs", icon: "📋" },
];

const AdminOverview = () => (
  <div className="h-a-p-page">
    <div className="h-a-p-content-section">
      <h2>📊 Admin Overview</h2>
      <div className="h-a-p-content-grid">
        <div className="h-a-p-content-card">
          <h3>Quick Stats</h3>
          <p>View your platform statistics and key metrics.</p>
        </div>
        <div className="h-a-p-content-card">
          <h3>Recent Activity</h3>
          <p>Monitor recent user and course activities.</p>
        </div>
      </div>
    </div>
  </div>
);

const AnalyticsDashboard = () => (
  <div className="h-a-p-page">
    <div className="h-a-p-content-section">
      <h2>📈 Analytics Dashboard</h2>
      <div className="h-a-p-content-grid">
        <div className="h-a-p-content-card">
          <h3>Performance Metrics</h3>
          <p>Track platform performance and engagement.</p>
        </div>
        <div className="h-a-p-content-card">
          <h3>Revenue Reports</h3>
          <p>View financial reports and revenue analytics.</p>
        </div>
      </div>
    </div>
  </div>
);

const AdminSettings = () => (
  <div className="h-a-p-page">
    <div className="h-a-p-content-section">
      <h2>⚙️ Admin Settings</h2>
      <div className="h-a-p-content-grid">
        <div className="h-a-p-content-card">
          <h3>Platform Settings</h3>
          <p>Configure global platform settings.</p>
        </div>
        <div className="h-a-p-content-card">
          <h3>Security</h3>
          <p>Manage security and privacy settings.</p>
        </div>
      </div>
    </div>
  </div>
);

const renderPage = (page) => {
  switch (page) {
    case "overview":
      return <AdminOverview />;
    case "users":
      return <UserManagement />;
    case "courses":
      return <CourseAdminManagement />;
    case "testimonials":
      return <TestimonialsManagement />;
    case "analytics":
      return <AnalyticsDashboard />;
    case "messages":
      return <AdminMessagesList />;
    case "leadership":
      return <LeadershipAdminPanel />;   // ← NEW
    case "settings":
      return <AdminSettings />;
    case "audit":
      return <AuditLogs />;
    default:
      return <AdminOverview />;
  }
};

const AdminDashboard = () => {
  const [activePage, setActivePage] = useState(() => {
    const savedPage = localStorage.getItem("adminDashboardActivePage");
    return savedPage || "overview";
  });

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("adminDashboardActivePage", activePage);
  }, [activePage]);

  useEffect(() => {
    sessionStorage.setItem("adminDashboardActivePage", activePage);
  }, [activePage]);

  useEffect(() => {
    const url = new URL(window.location);
    url.searchParams.set("section", activePage);
    window.history.replaceState({}, "", url);
  }, [activePage]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlSection = urlParams.get("section");
    if (urlSection && menuItems.some((item) => item.id === urlSection)) {
      setActivePage(urlSection);
    }
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setIsDrawerOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handlePageChange = (pageId) => {
    setActivePage(pageId);
    requestAnimationFrame(() => setIsDrawerOpen(false));
  };

  const toggleDrawer = () => setIsDrawerOpen((v) => !v);
  const closeDrawer = () => setIsDrawerOpen(false);

  return (
    <div className="h-a-p-wrapper">
      <header className="h-a-p-mobile-header">
        <button
          type="button"
          className="h-a-p-drawer-toggle"
          onClick={toggleDrawer}
          aria-label="Toggle menu"
          aria-expanded={isDrawerOpen}
        >
          <span className="h-a-p-hamburger-line" />
          <span className="h-a-p-hamburger-line" />
          <span className="h-a-p-hamburger-line" />
        </button>
        <span className="h-a-p-mobile-title">
          {menuItems.find((i) => i.id === activePage)?.label || "Admin"}
        </span>
      </header>

      <div
        className={`h-a-p-drawer-backdrop ${
          isDrawerOpen ? "h-a-p-drawer-backdrop-open" : ""
        }`}
        onClick={closeDrawer}
        aria-hidden="true"
      />

      <div className="h-a-p-container">
        <aside
          className={`h-a-p-sidebar ${
            isDrawerOpen ? "h-a-p-sidebar-open" : ""
          }`}
        >
          <div className="h-a-p-sidebar-inner">
            <div className="h-a-p-sidebar-header">
              <h2 className="h-a-p-sidebar-title">Admin Panel</h2>
              <button
                type="button"
                className="h-a-p-drawer-close"
                onClick={closeDrawer}
                aria-label="Close menu"
              >
                ✕
              </button>
            </div>

            <nav className="h-a-p-sidebar-nav">
              {menuItems.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => handlePageChange(item.id)}
                  className={`h-a-p-menu-item ${
                    activePage === item.id ? "h-a-p-menu-item-active" : ""
                  }`}
                >
                  <span className="h-a-p-menu-item-icon">{item.icon}</span>
                  <span className="h-a-p-menu-item-label">{item.label}</span>
                </button>
              ))}
            </nav>

            <div className="h-a-p-sidebar-footer">
              <small className="h-a-p-last-accessed">
                Last viewed:{" "}
                {menuItems.find((item) => item.id === activePage)?.label}
              </small>
            </div>
          </div>
        </aside>

        <main className="h-a-p-content" key={activePage}>
          {renderPage(activePage)}
        </main>
      </div>

      <Footer />
    </div>
  );
};

export default AdminDashboard;